import { getBlockContent, type StreamingBlock } from "@storytell/streaming";
import { TbBrandGithub, TbExternalLink, TbLoader2, TbPlug } from "solid-icons/tb";
import { createMemo, type JSX, Match, Show, Switch } from "solid-js";
import { Dynamic } from "solid-js/web";
import { twMerge } from "tailwind-merge";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { type IntegrationProvider, useChatRuntime } from "../runtime/ChatRuntime";
import { GoogleIcon } from "./googleIcon";
import { type IntegrationSuggestionPayload, parseIntegrationSuggestionPayload } from "./integrationSuggestionSchema";
import { LinearIcon } from "./linear/linearIcon";
import { NotionIcon } from "./notion/notionIcon";
import { SlackIcon } from "./slack/slackIcon";

const PROVIDER_ICONS: Record<IntegrationProvider, (props: { class?: string }) => JSX.Element> = {
  google: GoogleIcon,
  slack: SlackIcon,
  notion: NotionIcon,
  linear: LinearIcon,
  github: TbBrandGithub,
};

const CTA_CLASS =
  "mt-3 inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-on-brand transition-colors hover:bg-brand-hover disabled:cursor-not-allowed disabled:opacity-50";

const ConnectButton = (props: { name: string; icon: JSX.Element; busy?: boolean; onClick: () => void }) => {
  const rt = useChatRuntime();
  return (
    <button
      type="button"
      onClick={() => props.onClick()}
      disabled={props.busy}
      aria-busy={props.busy}
      class={CTA_CLASS}
    >
      <Show when={!props.busy} fallback={<TbLoader2 class="size-4 animate-spin" />}>
        {props.icon}
      </Show>
      <span>
        {props.busy ? rt.t("common.connecting") : rt.t("integration_suggestion_card.connect", { name: props.name })}
      </span>
    </button>
  );
};

const ErrorLine = (props: { message: string | null | undefined }) => (
  <Show when={props.message}>
    {(message) => (
      <p class="mt-2 text-xs text-red-500" role="alert">
        {message()}
      </p>
    )}
  </Show>
);

// bluesky runs the OAuth trip inline; Chief can't in its webview, so it opens
// the Connected accounts page in the browser instead.
const McpConnect = (props: { serverId: string; name: string; connectUrl: string }) => {
  const rt = useChatRuntime();
  const conn = rt.ui?.useMcpConnect?.();
  return (
    <Switch>
      <Match when={conn}>
        {(c) => (
          <>
            <ConnectButton
              name={props.name}
              icon={<TbPlug class="size-4" />}
              busy={c().isConnecting()}
              onClick={() => {
                rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "mcp", via: "button" });
                void c().connect(props.serverId);
              }}
            />
            <ErrorLine message={c().error?.()} />
          </>
        )}
      </Match>
      <Match when={rt.actions.openExternalUrl}>
        <ConnectButton
          name={props.name}
          icon={<TbExternalLink class="size-4" />}
          onClick={() => {
            rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "mcp", via: "button" });
            rt.actions.openExternalUrl?.(props.connectUrl);
          }}
        />
      </Match>
    </Switch>
  );
};

const GoogleConnect = (props: { name: string }) => {
  const rt = useChatRuntime();
  const conn = rt.ui?.useProviderConnect?.("google");
  return (
    <Show when={conn || rt.actions.connectProvider}>
      <ConnectButton
        name={props.name}
        icon={<GoogleIcon class="size-4" />}
        busy={conn?.isConnecting()}
        onClick={() => {
          rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: "google", via: "button" });
          if (conn) void conn.connect();
          else rt.actions.connectProvider?.("google");
        }}
      />
      <ErrorLine message={conn?.error?.()} />
    </Show>
  );
};

// First-party integrations are set up once for the whole organization, so a
// member is told who can do it instead of being sent to a page they can't use.
const OwnerSetup = (props: { provider: IntegrationProvider; name: string }) => {
  const rt = useChatRuntime();
  const settingsHref = () => (rt.ui?.isOrgAdmin?.() ? rt.actions.integrationSettingsHref?.(props.provider) : undefined);
  return (
    <Switch
      fallback={
        <p class="mt-3 text-xs text-muted-text">
          {rt.t("integration_suggestion_card.ask_owner", { name: props.name })}
        </p>
      }
    >
      <Match when={settingsHref()}>
        {(href) => (
          <a href={href()} class={CTA_CLASS}>
            <span>{rt.t("integration_suggestion_card.set_up", { name: props.name })}</span>
          </a>
        )}
      </Match>
      {/* Chief can't check the role, so everyone gets the settings page in the
          system browser. */}
      <Match when={rt.actions.connectProvider}>
        <button
          type="button"
          onClick={() => {
            rt.actions.trackFeature?.({ feature: "chat.connect_provider", action: props.provider, via: "button" });
            rt.actions.connectProvider?.(props.provider);
          }}
          class={CTA_CLASS}
        >
          <span>{rt.t("integration_suggestion_card.set_up", { name: props.name })}</span>
          <TbExternalLink class="size-4" />
        </button>
      </Match>
    </Switch>
  );
};

const SuggestionIcon = (props: { data: IntegrationSuggestionPayload }) => (
  <Switch>
    <Match when={props.data.kind === "mcp" && props.data}>
      {(data) => (
        <Show when={data().iconUrl} fallback={<TbPlug class="size-6 shrink-0 text-brand" />}>
          {(src) => <img src={src()} alt="" class="size-6 shrink-0 rounded" />}
        </Show>
      )}
    </Match>
    <Match when={props.data.kind === "builtin" && props.data}>
      {(data) => <Dynamic component={PROVIDER_ICONS[data().provider]} class="size-6 shrink-0 text-primary-text" />}
    </Match>
  </Switch>
);

// Google is the one first-party provider anyone can connect for themselves.
const SuggestionCta = (props: { data: IntegrationSuggestionPayload }) => (
  <Switch>
    <Match when={props.data.kind === "mcp" && props.data}>
      {(mcp) => <McpConnect serverId={mcp().serverId} name={mcp().name} connectUrl={mcp().connectUrl} />}
    </Match>
    <Match when={props.data.kind === "builtin" && props.data}>
      {(builtin) => (
        <Show
          when={builtin().provider === "google" && !builtin().ownerOnly}
          fallback={<OwnerSetup provider={builtin().provider} name={builtin().name} />}
        >
          <GoogleConnect name={builtin().name} />
        </Show>
      )}
    </Match>
  </Switch>
);

// The model calls suggest_integration when a request needs an integration the
// user doesn't have; the card carries its reason and a way to connect.
export const IntegrationSuggestionRenderer = (props: { block: StreamingBlock; flat?: boolean }) => {
  const rt = useChatRuntime();

  const rawContent = createMemo(
    () =>
      (props.block.finalContent?.type === "text" ? props.block.finalContent.text : undefined) ??
      getBlockContent(props.block.blockId)
  );

  const payload = createMemo(() => parseIntegrationSuggestionPayload(safeParseBlockJson(rawContent())));

  const boxClass = () =>
    twMerge(
      "rounded-lg border border-primary-border bg-panel p-4",
      props.flat && "rounded-none border-0 bg-transparent p-0"
    );

  return (
    <Show
      when={payload()}
      fallback={
        <Show when={rawContent()}>
          <div class={boxClass()}>
            <p class="text-xs text-muted-text">{rt.t("integration_suggestion_card.parse_error")}</p>
          </div>
        </Show>
      }
    >
      {(data) => (
        <div class={boxClass()}>
          <div class="flex items-start gap-3">
            <SuggestionIcon data={data()} />
            <div class="min-w-0 flex-1">
              <h3 class="text-sm font-medium text-primary-text">{data().name}</h3>
              <p class="mt-1 text-xs text-muted-text">{data().reason}</p>
            </div>
          </div>
          <SuggestionCta data={data()} />
        </div>
      )}
    </Show>
  );
};
